/**
 * IBSStoryHub (P275) -- the IBS entity-lifecycle hub.
 *
 * A single at-a-glance chip row on the IBS detail page telling the IBS's
 * own story: admission state, impact tolerance (+ signed?), MVB tier,
 * resilience RAG, obligations count, and the scenarios touching it. Each
 * chip deep-links to its ceremony / surface (kills F5 -- the lifecycle was
 * scattered across ~6 places). Reads GET /v1/twin/ibses/{id}/story.
 */
import { said } from "shared/i18n/said";
import { formatDate } from "shared/time/formatDate";
import type { TFunction } from "i18next";
import { Link } from "react-router-dom";
import { NoOwnerFlag } from "shared/components/NoOwnerFlag";
import { useTranslation } from "react-i18next";

import type { IBSStory } from "shared/api/types";
import { useIBSStory } from "twin/api/hooks";
import { useMe } from "shared/api/useMe";
import { useMyEstate } from "twin/home/useMyEstate";

import { SignatureHistory } from "shared/components/SignatureHistory";
type Tone = "green" | "amber" | "rose" | "indigo" | "slate";

const TONE_CLASS: Record<Tone, string> = {
  green: "border-emerald-200 bg-emerald-50 text-emerald-800",
  amber: "border-amber-200 bg-amber-50 text-amber-900",
  rose: "border-rose-200 bg-rose-50 text-rose-800",
  indigo: "border-indigo-200 bg-indigo-50 text-indigo-800",
  slate: "border-slate-200 bg-slate-50 text-slate-600",
};

interface Chip {
  key: string;
  label: string;
  value: string;
  tone: Tone;
  to?: string;
  /** D4 -- an element on THIS page to move to, for the case where the
   *  answer is beneath the chip and the register that would otherwise
   *  be linked refuses the reader. */
  scrollTo?: string;
  /** B2 -- a second line beneath the value, for the rare case where a
   *  recommendation differs from the record. Not a place for detail:
   *  the chip answers one question and this says the register would
   *  answer it differently. */
  note?: string;
}

function humanize(s: string): string {
  return s.replace(/_/g, " ");
}

function buildChips(
  story: IBSStory,
  t: TFunction,
  opts: { isAdmin: boolean; ownsIt: boolean; language?: string },
): Chip[] {
  const chips: Chip[] = [];
  const language = opts.language ?? "en";

  // Admission.
  const adm = story.admission;
  const admTone: Tone =
    story.admission?.status === "admitted"
      ? "green"
      : story.admission?.status === "removed"
        ? "rose"
        : "slate";
  /* Round 16 N5 -- a function's tile read "candidate · tier 0" and
     opened the IBS admission register, where the function is not
     listed, while its own register said "assessed: included · awaiting
     sign-off". A function's tile reads its Route 2 state, in the
     register's words, and opens the register at its row. */
  const isCif = story.service_class === "internal";
  const cifValue = (): string => {
    if (!adm || !adm.workflow_state) return t("modules.twin.cifs.decision.none");
    if (adm.workflow_state === "approved")
      return t("modules.twin.cifs.status.admitted");
    const decision = t(
      `modules.twin.cifs.decision.${adm.inclusion_decision ?? "included"}`,
    ).toLowerCase();
    return t(
      adm.workflow_state === "draft"
        ? "modules.twin.cifs.decision.pendingChallenge"
        : "modules.twin.cifs.decision.pending",
      { decision },
    );
  };
  chips.push({
    key: "admission",
    label: t("modules.twin.ibsHub.admission"),
    value: isCif
      ? cifValue()
      : adm
        ? // Round 16 I14 -- in the reader's words, not the enum's.
          `${said(t, `modules.twin.ibsHub.status.${adm.status}`, humanize(adm.status))}${
            adm.criticality_tier
              ? ` · ${said(t, `modules.twin.ibsHub.tier.${adm.criticality_tier}`, humanize(adm.criticality_tier))}`
              : ""
          }`
        : t("modules.twin.ibsHub.notAdmitted"),
    tone: admTone,
    to: isCif ? `/twin/cifs?cif=${story.id}` : "/twin/ibs-admission",
  });

  // Tolerance.
  const tol = story.tolerance;
  chips.push({
    key: "tolerance",
    label: t("modules.twin.ibsHub.tolerance"),
    // C1 -- "not set" was shown for an IBS whose owner had declared a
    // tolerance an hour earlier and was waiting on the CRO. The column
    // does not fill until the second signature; saying nothing exists
    // in the meantime erases the work and the person who did it.
    value:
      tol.status === "pending"
        ? t("modules.twin.ibsHub.pending", { hours: tol.proposed_hours })
        : tol.hours == null
          ? t("modules.twin.ibsHub.notSet")
          : t("modules.twin.ibsHub.hours", { hours: tol.hours }) +
            ` · ${t(tol.signed ? "modules.twin.ibsHub.signed" : "modules.twin.ibsHub.notSigned")}`,
    tone:
      tol.status === "pending"
        ? "indigo"
        : tol.hours == null
          ? "slate"
          : tol.signed
            ? "green"
            : "amber",
    // D4 -- this linked into the admin register for everybody, so the
    // owner -- the one seat the panel below exists for -- got "Admin
    // role required" above a panel that works. Fourth round.
    //
    // An administrator keeps the register, because that is where he
    // changes it. An owner is moved to his own panel. Anyone else gets
    // no link at all: a chip that opens a wall is worse than a chip
    // that does not move, because it reads as the product refusing
    // rather than as nothing to see.
    // C8 -- and the Resilience Lead, whose job this IS, got the empty
    // branch: the one chip on the hub about her own subject was the
    // only dead one, while ADMISSION, MVB, RESILIENCE and SCENARIOS
    // beside it all moved. She does not edit the register and does not
    // own the service; she reads tolerance across the estate, and
    // there is a surface for exactly that.
    // Round 10 F3 -- and the last branch was an empty object, so for
    // everyone who is not an admin, the owner or the resilience lead
    // this chip stayed dead while the four beside it moved. Fourth
    // round. The destination it was withholding is
    // `/v1/twin/tolerance/metrics`, gated on the twin module alone:
    // every reader of this page can already open it. Withholding a
    // route somebody is allowed to walk is not a permission boundary,
    // it is a chip that does nothing.
    ...(opts.isAdmin
      ? { to: `/foundation/admin/ibses?edit=${story.id}` }
      : opts.ownsIt
        ? { scrollTo: "owner-ibs-panel" }
        : // B4 -- carry the IBS, so the page can say something about
          // the one they came from rather than showing twelve others.
          { to: `/twin/tolerance-metrics?ibs=${story.id}` }),
  });

  // MVB.
  //
  // B2 -- MEMBERSHIP, not the candidate engine's recommendation. This
  // chip read the tier when there was one and the flag only when there
  // was not, so SAMA regulatory reporting -- in the approved
  // declaration, recommended for exclusion by the register -- said
  // "Out of scope" on its own page while the war room, the cascade and
  // the portfolio all called it MVB. One of those is what a regulator
  // is told; the other is an opinion about the next declaration.
  chips.push({
    key: "mvb",
    label: t("modules.twin.ibsHub.mvb"),
    /* A4 -- the chip read "In the approved declaration" from the flag
       alone, so on the cold start it said so beside a portfolio
       reading "2 IBSes - 0 admitted - 2 MVB" with no declaration
       anywhere in the estate. The flag is what an approved ceremony
       PROJECTS; it is also settable by CSV, so it cannot be taken as
       proof the ceremony happened. Where it has not, the chip says
       what is true and no more. */
    value: !story.mvb.flag
      ? t("modules.twin.ibsHub.notMvb")
      : story.mvb.declaration_approved
        ? t("modules.twin.ibsHub.inDeclaration")
        : t("modules.twin.ibsHub.markedNotDeclared"),
    // The recommendation earns its space only where it DIFFERS -- that
    // is the case worth a reader's attention, and the register is one
    // click away for the rest.
    ...(story.mvb.recommendation_differs
      ? {
          note: t("modules.twin.ibsHub.recommends", {
            tier: humanize(story.mvb.tier ?? ""),
          }),
        }
      : {}),
    // Amber where the estate is asserting membership no ceremony has
    // granted: not an error, but not a settled fact either.
    tone: !story.mvb.flag
      ? "slate"
      : story.mvb.declaration_approved
        ? "indigo"
        : "amber",
    to: "/twin/mvb-candidates",
  });

  /* Resilience RAG.
     Round 12 B1 -- an IBS page read "RESILIENCE green" on a tenant with
     no telemetry, no declared tolerance and nothing admitted. The seal
     is not wrong: the deriver seals green for a service with no open
     finding. What the word claims is that the service is healthy, and
     what the estate supports is that nothing has reported a problem.
     Green is withheld until there is something looking. */
  const sealed = story.resilience?.state ?? "unknown";
  const state =
    sealed === "green" && story.resilience?.estate_assessed === false
      ? "not_assessed"
      : sealed;
  const resTone: Tone =
    state === "red"
      ? "rose"
      : state === "amber"
        ? "amber"
        : state === "green"
          ? "green"
          : "slate";
  chips.push({
    key: "resilience",
    label: t("modules.twin.ibsHub.resilience"),
    value: t(`modules.twin.ibsHub.rag.${state}`),
    tone: resTone,
    to: "/twin/cascade",
  });

  // Obligations -- a plain count; the ObligationsPanel sits right below.
  chips.push({
    key: "obligations",
    label: t("modules.twin.ibsHub.obligations"),
    value: String(story.obligations_count),
    tone: "slate",
  });

  // Scenarios.
  chips.push({
    key: "scenarios",
    label: t("modules.twin.ibsHub.scenarios"),
    value: String(story.scenarios_count),
    tone: story.scenarios_count > 0 ? "amber" : "slate",
    to: "/twin/scenarios",
  });

  // P428.2: the group this service is governed in. Ungrouped only
  // matters once admitted -- grouping follows admission.
  if (story.group_name) {
    chips.push({
      key: "group",
      label: t("modules.twin.ibsHub.group"),
      value: story.group_name,
      tone: "slate",
      /* Round 16 O5 -- to the group itself, not the top of the tab. */
      to: story.group_code
        ? `/twin/ibs-groups#${story.group_code}`
        : "/twin/ibs-groups",
    });
  } else if (story.admission?.status === "admitted") {
    chips.push({
      key: "group",
      label: t("modules.twin.ibsHub.group"),
      value: t("modules.twin.ibsHub.ungrouped"),
      tone: "amber",
      to: "/foundation/admin/ibs-groups",
    });
  }

  // P421 / round 19 G40 -- the changes on this service. Said when there
  // are none: a missing chip let the owner read "nothing changed" and
  // "change is not shown here" as the same page.
  const change = story.change;
  const touching = change ? (change.touching_count ?? change.reached_count) : 0;
  if (change && touching > 0 && change.latest_title && change.latest_raised_at) {
    chips.push({
      key: "change",
      label: t("modules.twin.changes.chipLabel"),
      value: change.latest_title,
      note:
        touching > 1
          ? t("modules.twin.changes.chipNoteMore", {
              date: formatDate(change.latest_raised_at, language),
              count: touching - 1,
            })
          : t("modules.twin.changes.chipNote", {
              date: formatDate(change.latest_raised_at, language),
            }),
      tone: change.latest_significance === "routine" ? "slate" : "amber",
      // For the owner, the register opens on every service he owns.
      to: opts.ownsIt
        ? `/twin/changes?ibs=${story.id}&mine=1`
        : `/twin/changes?ibs=${story.id}`,
    });
  } else {
    chips.push({
      key: "change",
      label: t("modules.twin.changes.chipLabel"),
      value: t("modules.twin.changes.chipNone"),
      tone: "slate",
      to: "/twin/changes",
    });
  }

  return chips;
}

function ChipView({ chip }: { chip: Chip }) {
  const body = (
    <>
      <span className="text-[9px] font-semibold uppercase tracking-wide opacity-70">
        {chip.label}
      </span>
      <span className="text-xs font-medium">{chip.value}</span>
      {chip.note && <span className="text-[9px] italic opacity-70">{chip.note}</span>}
    </>
  );
  const cls = `flex flex-col gap-0.5 rounded-md border px-2.5 py-1.5 ${TONE_CLASS[chip.tone]}`;
  if (chip.scrollTo) {
    return (
      <button
        type="button"
        data-testid={`ibs-hub-chip-${chip.key}`}
        data-scroll-to={chip.scrollTo}
        onClick={() =>
          document
            .getElementById(chip.scrollTo!)
            ?.scrollIntoView({ behavior: "smooth", block: "start" })
        }
        className={`${cls} text-start transition hover:brightness-95`}
      >
        {body}
      </button>
    );
  }
  return chip.to ? (
    <Link
      to={chip.to}
      data-testid={`ibs-hub-chip-${chip.key}`}
      className={`${cls} transition hover:brightness-95`}
    >
      {body}
    </Link>
  ) : (
    <span data-testid={`ibs-hub-chip-${chip.key}`} className={cls}>
      {body}
    </span>
  );
}

export function IBSStoryHub({ ibsId }: { ibsId: string | undefined }) {
  const { t, i18n } = useTranslation();
  const query = useIBSStory(ibsId);
  const me = useMe();
  // The same estate query the panel below uses, so this is a cache read
  // rather than a second fetch.
  const estate = useMyEstate();
  const ownsIt = (estate.data?.ibses ?? []).some((i) => i.id === ibsId);

  if (query.isLoading || !query.data) return null; // degrade silently
  const chips = buildChips(query.data, t, {
    isAdmin: me.data?.is_admin ?? false,
    ownsIt,
    language: i18n.language,
  });

  return (
    <section data-testid="ibs-story-hub" className="space-y-2">
      <h2 className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-slate-400">
        {t(
          query.data.service_class === "internal"
            ? "modules.twin.ibsHub.headingInternal"
            : "modules.twin.ibsHub.heading",
        )}
      </h2>
      <div className="flex flex-wrap gap-2">
        {chips.map((c) => (
          <ChipView key={c.key} chip={c} />
        ))}
      </div>
      {/* Round 20 V5 -- admitted, and nobody recorded as accountable. */}
      {query.data.admission?.status === "admitted" &&
        query.data.owner_recorded === false && (
          <p data-testid="ibs-hub-no-owner" className="text-sm text-rose-800">
            <NoOwnerFlag openCritical={query.data.open_critical ?? 0} />
          </p>
        )}
      {/* Round 16 M4 -- what stops when this stops. The owner's page
          listed what funds his function and never what it funds, so
          the question he owns it for had no answer on it. */}
      {query.data.service_class === "internal" && (
        <p data-testid="ibs-hub-funds" className="text-sm text-slate-700">
          {(query.data.funds ?? []).length > 0 ? (
            <>
              {t("modules.twin.ibsHub.funds.lead")}{" "}
              {(query.data.funds ?? []).map((f, i) => (
                <span key={f.id}>
                  {i > 0 ? ", " : ""}
                  <Link
                    to={`/twin/ibs/${f.id}`}
                    className="text-brand-brassDark hover:underline"
                  >
                    {f.name}
                  </Link>
                  {!f.admitted && (
                    <span className="text-xs text-slate-500">
                      {" "}
                      {t("modules.twin.ibsHub.funds.notAdmitted")}
                    </span>
                  )}
                </span>
              ))}
            </>
          ) : (
            t("modules.twin.ibsHub.funds.none")
          )}
        </p>
      )}
      {/* C1 -- the hub named the state an IBS was admitted in and
          showed none of the signatures that put it there, on the one
          page meant to answer what the state of this IBS is. Legs that
          have not happened are dropped upstream, so a ceremony
          mid-flight shows what it has rather than blanks for what it
          has not. */}
      {query.data.admission?.signatures &&
        query.data.admission.signatures.length > 0 && (
          <SignatureHistory
            testid="ibs-hub-signatures"
            entries={query.data.admission.signatures.map((leg) => ({
              roleLabel: t(`roles.${leg.role}`, { defaultValue: leg.role }),
              name: leg.name,
              at: leg.at,
              note: leg.note,
              // Round 10 F2 -- the same rule the admission register
              // uses, because it is the same ceremony: the challenge
              // form takes words, the two sign legs do not.
              commentOffered: leg.role === "challenge",
            }))}
          />
        )}
    </section>
  );
}
